import axios, { type AxiosRequestConfig, type AxiosResponse } from 'axios'
import { message } from 'ant-design-vue'
import qs from 'qs'
import { useUserStore } from '@/store/user'

/**
 * API 响应基础接口
 */
export interface ApiResponse<T = any> {
  code: number
  data: T
  message: string
  timestamp?: number
  success?: boolean
}

/**
 * 分页数据接口
 */
export interface PageData<T = unknown> {
  list: T[]
  total: number
  pageNum: number
  pageSize: number
  size: number
  startRow: number
  endRow: number
  pages: number
  prePage: number
  nextPage: number
  isFirstPage: boolean
  isLastPage: boolean
  hasPreviousPage: boolean
  hasNextPage: boolean
  navigatePages: number
  navigatepageNums: number[]
  navigateFirstPage: number
  navigateLastPage: number
}

/**
 * 分页响应接口
 */
export interface PageResponse<T = unknown> extends ApiResponse<PageData<T>> {
  data: PageData<T>
}

/**
 * 列表响应接口（不带分页）
 */
export interface ListResponse<T = unknown> extends ApiResponse<T[]> {
  data: T[]
}

/**
 * 通用响应接口（无数据）
 */
export interface EmptyResponse extends ApiResponse<null> {
  data: null
}

type TypedRequest = Omit<ReturnType<typeof axios.create>, 'get' | 'post' | 'put' | 'delete' | 'patch'> & {
  <T = any>(config: AxiosRequestConfig): Promise<T>
  request<T = any>(config: AxiosRequestConfig): Promise<T>

  get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T>
  get(url: string, config: AxiosRequestConfig & { responseType: 'blob' }): Promise<Blob>
  get(url: string, config: AxiosRequestConfig & { responseType: 'arraybuffer' }): Promise<ArrayBuffer>

  post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T>
  put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T>
  patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T>
  delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T>
}

// 创建 axios 实例
const request = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '',
  timeout: 30000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json;charset=UTF-8',
  },
}) as TypedRequest

// 请求拦截器
request.interceptors.request.use(
  (config) => {
    // 1. 添加 Token 到请求头
    const userStore = useUserStore()
    if (userStore.token) {
      config.headers.Authorization = `Bearer ${userStore.token}`
      config.headers['X-Auth-Token'] = userStore.token
    }

    // 2. 如果是 FormData（文件上传），删除 Content-Type 让浏览器自动设置 multipart boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type']
    }

    // 3. 如果是表单格式请求，转换数据
    if (
      config.data &&
      !(config.data instanceof FormData) &&
      (config.method === 'post' || config.method === 'put' || config.method === 'patch') &&
      config.headers['Content-Type'] === 'application/x-www-form-urlencoded;charset=UTF-8'
    ) {
      config.data = qs.stringify(config.data)
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  },
)

// 响应拦截器
request.interceptors.response.use(
  (response: AxiosResponse) => {
    const { data } = response

    const responseType = response.config?.responseType
    if (responseType === 'blob' || responseType === 'arraybuffer') {
      return data
    }

    // 处理业务错误码 - 401 未认证
    if (data.code === 401) {
      // 先清除用户信息，避免循环跳转
      const userStore = useUserStore()
      userStore.clearUserInfo()
      userStore.clearToken()

      message.error({
        content: '登录过期，请重新登录！',
        key: 'auth-error',
      })

      // 避免重复跳转
      if (!window.location.pathname.startsWith('/login')) {
        window.location.href = '/login'
      }
      return Promise.reject(new Error(data.message || '未授权'))
    }

    // 处理业务错误码 - 403 权限不足（不清除登录态）
    if (data.code === 403) {
      message.error({
        content: data.message || '您没有权限执行此操作',
        key: 'permission-error',
      })
      return Promise.reject(new Error(data.message || '权限不足'))
    }

    return data
  },
  (error) => {
    // 判断是否是请求取消错误（快速切换页面导致）
    if (error.code === 'ERR_CANCELED' || error.message?.includes('canceled') || error.message?.includes('aborted')) {
      // 请求被取消是正常行为，不显示错误提示
      console.debug('请求已取消:', error.config?.url)
      return Promise.reject(error)
    }

    // HTTP 错误处理
    if (error.response) {
      const { status } = error.response
      if (status === 401) {
        // 401 未认证 - 清除登录态跳转登录页
        const userStore = useUserStore()
        userStore.clearUserInfo()
        userStore.clearToken()

        message.error({
          content: '登录过期，请重新登录！',
          key: 'auth-error',
        })

        // 避免重复跳转
        if (!window.location.pathname.startsWith('/login')) {
          window.location.href = '/login'
        }
      } else if (status === 403) {
        // 403 权限不足 - 只提示，不清除登录态
        message.error({
          content: error.response.data?.message || '您没有权限执行此操作',
          key: 'permission-error',
        })
      } else {
        message.error({
          content: error.response.data?.message || '请求失败',
          key: 'request-error',
        })
      }
    } else if (
      error.code === 'ECONNABORTED' ||
      error.code === 'ETIMEDOUT' ||
      error.message?.toLowerCase().includes('timeout')
    ) {
      message.error({
        content: '请求超时，请稍后重试',
        key: 'timeout-error',
      })
    } else if (error.request) {
      message.error({
        content: '网络错误，请检查网络连接',
        key: 'network-error',
      })
    }
    return Promise.reject(error)
  },
)

// 导出请求方法
export default request

/**
 * HTTP 请求便捷方法
 */
export const http = {
  /**
   * GET 请求
   */
  get<T = any>(url: string, params?: any): Promise<ApiResponse<T>> {
    return request.get<ApiResponse<T>>(url, { params })
  },

  /**
   * POST 请求（JSON 格式，默认）
   */
  post<T = any>(url: string, data?: any): Promise<ApiResponse<T>> {
    return request.post<ApiResponse<T>>(url, data)
  },

  /**
   * PUT 请求（JSON 格式，默认）
   */
  put<T = any>(url: string, data?: unknown): Promise<ApiResponse<T>> {
    return request.put<ApiResponse<T>>(url, data)
  },

  /**
   * PATCH 请求（JSON 格式）
   */
  patch<T = any>(url: string, data?: unknown): Promise<ApiResponse<T>> {
    return request.patch<ApiResponse<T>>(url, data)
  },

  /**
   * DELETE 请求
   */
  delete<T = any>(url: string, params?: Record<string, unknown>): Promise<ApiResponse<T>> {
    return request.delete<ApiResponse<T>>(url, { params })
  },

  /**
   * POST 请求（JSON 格式）- 与 post 相同，保留兼容性
   */
  postJSON<T = any>(url: string, data?: Record<string, unknown>): Promise<ApiResponse<T>> {
    return request.post<ApiResponse<T>>(url, data)
  },

  /**
   * PUT 请求（JSON 格式）- 与 put 相同，保留兼容性
   */
  putJSON<T = any>(url: string, data?: unknown): Promise<ApiResponse<T>> {
    return request.put<ApiResponse<T>>(url, data)
  },

  /**
   * POST 请求（表单格式）
   */
  postForm<T = any>(url: string, data?: Record<string, unknown>): Promise<ApiResponse<T>> {
    return request.post<ApiResponse<T>>(url, data, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
      },
    })
  },

  /**
   * PUT 请求（表单格式）
   */
  putForm<T = any>(url: string, data?: Record<string, unknown>): Promise<ApiResponse<T>> {
    return request.put<ApiResponse<T>>(url, data, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
      },
    })
  },

  /**
   * 分页查询（GET）
   */
  getPage<T = any>(
    url: string,
    params?: { start?: number; limit?: number } & Record<string, unknown>
  ): Promise<PageResponse<T>> {
    return request.get<PageResponse<T>>(url, { params })
  },

  /**
   * 列表查询（GET，不带分页）
   */
  getList<T = any>(url: string, params?: Record<string, unknown>): Promise<ListResponse<T>> {
    return request.get<ListResponse<T>>(url, { params })
  },
}
